import { memo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import rehypeSanitize from "rehype-sanitize";
import { Copy, Check } from "lucide-react";
import type { Message } from "@shared/types";
import { cn } from "@/lib/utils";
import { CitationBlock } from "./CitationBlock";
import { FeedbackButtons } from "./FeedbackButtons";

interface SourceInfo {
  skill_name: string;
  category: string;
  source_file: string;
  chunk_type: string;
}

interface MessageBubbleProps {
  message: Message;
  isStreaming?: boolean;
  streamingContent?: string;
  onFeedback?: (messageId: string, feedback: "up" | "down" | null) => void;
}

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

export const MessageBubble = memo(function MessageBubble({
  message,
  isStreaming,
  streamingContent,
  onFeedback,
}: MessageBubbleProps) {
  const isUser = message.role === "user";
  const content = isStreaming ? (streamingContent ?? "") : message.content;
  const sources = (message.sources ?? []) as unknown as SourceInfo[];
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      console.warn("Clipboard copy failed");
    }
  };

  return (
    <div
      role="article"
      aria-label={isUser ? "Sua mensagem" : "Mensagem do assistente"}
      className={cn(
        "flex w-full gap-3",
        isUser ? "justify-end" : "justify-start",
      )}
    >
      <div
        className={cn(
          "group relative max-w-[85%] rounded-2xl px-4 py-3",
          isUser
            ? "bg-primary text-primary-foreground"
            : "bg-muted text-foreground",
        )}
      >
        {/* Copy button — always visible on mobile, hover-reveal on desktop for user msgs */}
        {!isStreaming && content.length > 0 && (
          <button
            type="button"
            onClick={handleCopy}
            className={cn(
              "absolute right-1 top-1 inline-flex h-11 w-11 items-center justify-center rounded-md bg-background/90 text-muted-foreground shadow-sm ring-1 ring-border transition-opacity duration-200 hover:text-foreground sm:h-7 sm:w-7",
              isUser
                ? "opacity-70 sm:opacity-0 sm:group-hover:opacity-100"
                : "opacity-70 hover:opacity-100",
            )}
            aria-label="Copiar mensagem"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-green-500" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        )}

        {isUser ? (
          <p className="whitespace-pre-wrap break-words text-sm">{content}</p>
        ) : (
          <div className="prose prose-sm max-w-none break-words overflow-y-auto max-h-[60vh] [&_pre]:overflow-x-auto [&_pre]:bg-background/50 [&_pre]:rounded-lg [&_pre]:p-3 [&_code]:text-xs [&_p]:text-foreground [&_li]:text-foreground [&_a]:text-primary [&_strong]:text-foreground [&_h1]:text-foreground [&_h2]:text-foreground [&_h3]:text-foreground">
            {isStreaming && !content && (
              <div className="space-y-2">
                <div className="h-3 w-3/4 animate-pulse rounded bg-muted-foreground/20" />
                <div className="h-3 w-1/2 animate-pulse rounded bg-muted-foreground/20" />
                <div className="h-3 w-2/3 animate-pulse rounded bg-muted-foreground/20" />
              </div>
            )}
            {content && (
              <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight, rehypeSanitize]}>
                {content}
              </ReactMarkdown>
            )}
            {isStreaming && (
              <span className="inline-block h-4 w-0.5 animate-pulse bg-foreground" />
            )}
          </div>
        )}

        <span className="mt-1 block text-[10px] opacity-50">
          {formatTime(message.created_at)}
        </span>

        {/* Citations below assistant messages */}
        {!isUser && !isStreaming && sources.length > 0 && (
          <CitationBlock sources={sources} />
        )}

        {/* Feedback below assistant messages */}
        {!isUser && !isStreaming && onFeedback && (
          <FeedbackButtons
            messageId={message.id}
            currentFeedback={message.feedback}
            onFeedback={onFeedback}
          />
        )}
      </div>
    </div>
  );
});
